import api from './api';

// 系统管理相关类型定义
export interface SystemConfig {
  system_name: string;
  version: string;
  max_clients: number;
  max_sub_accounts_per_client: number;
  max_links_per_client: number;
  jwt_expire_hours: number;
  database: {
    host: string;
    port: number;
    database: string;
  };
  redis: {
    host: string;
    port: number;
  };
  feature_flags?: {
    enable_pixel_feature?: boolean;
    enable_billing?: boolean;
    enable_ip_stats?: boolean;
    enable_operation_logs?: boolean;
  };
}

export interface SystemMonitor {
  cpu_usage: string;
  memory_usage: string;
  disk_usage: string;
  network_in: string;
  network_out: string;
  active_connections: number;
  uptime: string;
  database: {
    connections: number;
    queries_per_second: number;
    slow_queries: number;
  };
  redis: {
    connections: number;
    memory_usage: string;
    keys: number;
  };
  services: {
    admin_server: string;
    redirect_server: string;
    database: string;
    redis: string;
  };
}

export interface SystemLog {
  id: number;
  level: string;
  message: string;
  timestamp: string;
  module: string;
}

export interface SystemLogsResponse {
  list: SystemLog[];
  total: number;
  page: number;
  size: number;
}

export interface DatabaseStats {
  database_size: number;
  table_stats: Array<{
    table_name: string;
    table_rows: number;
    size_mb: number;
  }>;
}

export interface PerformanceStats {
  recent_access: Array<{
    date: string;
    count: number;
  }>;
  avg_response_time: number;
  error_rate: number;
  total_requests: number;
  error_requests: number;
}

export interface SecurityStats {
  login_failures: number;
  suspicious_ips: number;
  crawler_access: number;
  malicious_requests: number;
}

export interface BackupStatus {
  last_backup: string;
  backup_size: string;
  backup_status: string;
  next_backup: string;
  backup_retention: string;
  auto_backup: boolean;
}

export interface CreateBackupResponse {
  backup_id: string;
  backup_size: string;
  backup_path: string;
}

// 系统管理API服务
export const systemService = {
  // 获取系统配置
  getSystemConfig: async (): Promise<SystemConfig> => {
    const response = await api.post('/api/admin/system/config');
    return response.data.data;
  },

  // 更新系统配置
  updateSystemConfig: async (config: Record<string, any>): Promise<void> => {
    await api.post('/api/admin/system/config/update', { config });
  },

  // 获取系统日志
  getSystemLogs: async (params?: {
    page?: number;
    size?: number;
    level?: string;
  }): Promise<SystemLogsResponse> => {
    const response = await api.post('/api/system/logs', params);
    return response.data.data;
  },

  // 获取系统监控
  getSystemMonitor: async (): Promise<SystemMonitor> => {
    const response = await api.post('/api/system/monitor');
    return response.data.data;
  },

  // 获取数据库统计
  getDatabaseStats: async (): Promise<DatabaseStats> => {
    const response = await api.post('/api/system/database/stats');
    return response.data.data;
  },

  // 获取性能统计
  getPerformanceStats: async (): Promise<PerformanceStats> => {
    const response = await api.post('/api/system/performance/stats');
    return response.data.data;
  },

  // 获取安全统计
  getSecurityStats: async (): Promise<SecurityStats> => {
    const response = await api.post('/api/system/security/stats');
    return response.data.data;
  },

  // 获取备份状态
  getBackupStatus: async (): Promise<BackupStatus> => {
    const response = await api.post('/api/system/backup/status');
    return response.data.data;
  },

  // 创建备份
  createBackup: async (): Promise<CreateBackupResponse> => {
    const response = await api.post('/api/system/backup/create');
    return response.data.data;
  },
}; 